// src/screens/Home.js
import React, { useState, useEffect } from "react";
import { useAuth } from "../hooks/useAuth";
import { useGeoFence } from "../hooks/useGeoFence";
import { checkIn, checkOut, getStatus } from "../api";
import { toast } from "react-toastify";

export default function Home() {
  const { user } = useAuth();
  const isiOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const { allowed: inRange, permissionState, debugInfo } = useGeoFence(400);

  const [doneIn, setDoneIn]     = useState(false);
  const [doneOut, setDoneOut]   = useState(false);
  const [loadingIn, setLoadingIn]   = useState(false);
  const [loadingOut, setLoadingOut] = useState(false);

  // Fetch today’s status
  useEffect(() => {
    async function fetchStatus() {
      try {
        const res = await getStatus(user.username, user.deviceId);
        setDoneIn(res.checkIn);
        setDoneOut(res.checkOut);
      } catch (err) {
        console.error(err);
      }
    }
    fetchStatus();
  }, [user]);

  // Common distance check for iOS
  const withLocationCheck = (actionFn) => {
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const toRad = d => (d * Math.PI) / 180;
        const R = 6371e3;
        const dLat = toRad(coords.latitude - OFFICE.lat);
        const dLon = toRad(coords.longitude - OFFICE.lon);
        const a =
          Math.sin(dLat / 2) ** 2 +
          Math.cos(toRad(OFFICE.lat)) *
          Math.cos(toRad(coords.latitude)) *
          Math.sin(dLon / 2) ** 2;
        const dist = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        if (dist > 400) { // <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<< Range 
          toast.error("Too far from office");
          return;
        }
        actionFn();
      },
      (err) => {
        toast.error("Location error: " + err.message);
      },
      { enableHighAccuracy: true }
    );
  };

  // Handlers
  const handleCheckIn = () => {
    const doCheckIn = async () => {
      setLoadingIn(true);
      try {
        const res = await checkIn(user.username, user.deviceId);
        if (res.success) {
          toast.success("Checked in!");
          setDoneIn(true);
        } else {
          toast.error(res.error);
        }
      } catch {
        toast.error("Network error");
      } finally {
        setLoadingIn(false);
      }
    };

    if (isiOS) {
      withLocationCheck(doCheckIn);
    } else {
      if (!inRange) return toast.error("Too far from office");
      doCheckIn();
    }
  };

  const handleCheckOut = () => {
    const doCheckOut = async () => {
      setLoadingOut(true);
      try {
        const res = await checkOut(user.username, user.deviceId);
        if (res.success) {
          toast.success("Checked out!");
          setDoneOut(true);
        } else {
          toast.error(res.error);
        }
      } catch {
        toast.error("Network error");
      } finally {
        setLoadingOut(false);
      }
    };

    if (isiOS) {
      withLocationCheck(doCheckOut);
    } else {
      if (!inRange) return toast.error("Too far from office");
      doCheckOut();
    }
  };

  // Styles
  const styles = {
    container: {
      maxWidth: 360,
      margin: "40px auto",
      padding: 20,
      background: "#fff",
      borderRadius: 12,
      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
      fontFamily: "sans-serif",
      textAlign: "center",
    },
    heading: {
      marginBottom: 24,
      color: "#333",
      fontSize: 20,
      fontWeight: 500,
    },
    button: (disabled) => ({
      width: "100%",
      padding: 14,
      marginBottom: 12,
      border: "none",
      borderRadius: 6,
      background: disabled ? "#ccc" : "#1976d2",
      color: "#fff",
      fontSize: 16,
      fontWeight: 600,
      cursor: disabled ? "not-allowed" : "pointer",
    }),
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.heading}>Welcome!, {user.username}</h2>

      <button
        style={styles.button(doneIn || loadingIn)}
        onClick={handleCheckIn}
        disabled={doneIn || loadingIn}
      >
        {loadingIn
          ? "Checking In..."
          : doneIn
          ? "Checked In"
          : "Check In"}
      </button>

      <button
        style={styles.button(!doneIn || doneOut || loadingOut)}
        onClick={handleCheckOut}
        disabled={!doneIn || doneOut || loadingOut}
      >
        {loadingOut
          ? "Checking Out..."
          : doneOut
          ? "Checked Out"
          : "Check Out"}
      </button>

      {/* DEBUG INFO */}
      {debugInfo && (
        <div style={{ marginTop: 20, fontSize: 13, color: "#444", textAlign: "left" }}>
          <div><b>📍 User:</b> {debugInfo.userLat}, {debugInfo.userLon}</div>
          <div><b>🏢 Office:</b> {debugInfo.officeLat}, {debugInfo.officeLon}</div>
          <div><b>📏 Distance:</b> {debugInfo.distance} meters</div>
          <div><b>🎯 Accuracy:</b> ±{debugInfo.accuracy} meters</div>
          {debugInfo.error && <div style={{ color: "red" }}>❌ {debugInfo.error}</div>}
        </div>
      )}
    </div>
  );
}

// Office coords
const OFFICE = { lat: 29.99709, lon: 31.52856 }; 
// dont forget to update OFFICE in useGeoFence.js for Android


